import { useEffect } from "react";

// Drawers are opened/closed by React state, but their grey backdrop is created
// by Flowbite (data-drawer-show) and only removed when Flowbite's own hide()
// runs. When React closes a drawer without Flowbite knowing (click-outside,
// navigating away, a close button pointing at the wrong drawer id), the
// backdrop is left behind and blocks every click until a page reload.
//
// This watches the DOM and, once no drawer is open, hides any Flowbite drawer
// instance that still thinks it is visible (so it creates a fresh backdrop
// next time) and removes leftover backdrops. All drawers are right-placed, so
// a closed drawer has `translate-x-full`. Only the `fixed` panels count:
// their inner headings also use ids like `drawer-label`.
function isAnyDrawerOpen() {
  return Array.from(document.querySelectorAll('[id^="drawer-"].fixed')).some(
    (el) => !el.classList.contains("translate-x-full")
  );
}

function removeOrphanBackdrops() {
  const backdrops = document.querySelectorAll("[drawer-backdrop]");
  const staleDrawers = Object.values(
    window.FlowbiteInstances?.getInstances("Drawer") ?? {}
  ).filter((drawer) => drawer.isVisible());
  if (backdrops.length === 0 && staleDrawers.length === 0) return;
  if (isAnyDrawerOpen()) return;

  staleDrawers.forEach((drawer) => drawer.hide());
  document.querySelectorAll("[drawer-backdrop]").forEach((el) => el.remove());
  document.body.classList.remove("overflow-hidden");
}

export default function useDrawerBackdropCleanup() {
  useEffect(() => {
    const observer = new MutationObserver(removeOrphanBackdrops);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
}
